import {
  Badge,
  BlockStack,
  Box,
  IndexTable,
  InlineStack,
  Text,
  useIndexResourceState,
} from '@shopify/polaris';
import { SparklineCell } from './SparklineCell';
import { formatTime } from '../shared/useNotificationFormState';
import type { NotificationItem } from '@/queries/notifications';

export interface HistoryTableProps {
  notifications: NotificationItem[];
}

export function HistoryTable({ notifications }: HistoryTableProps) {
  const resourceName = { singular: 'notification', plural: 'notifications' };
  const itemKeys = notifications.map((n) => ({ id: String(n.id) }));
  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(itemKeys);

  const rows = notifications.map((n, index) => {
    const openRate = n.sent_count ? Math.round((n.opened_count / n.sent_count) * 100) : 0;
    const clickRate = n.sent_count ? Math.round((n.clicked_count / n.sent_count) * 100) : 0;
    const tone =
      n.status === 'sent'
        ? 'success'
        : n.status === 'partial'
        ? 'warning'
        : n.status === 'failed'
        ? 'critical'
        : 'info';

    // Mock 7-day trend — derived from current stats since we don't have history per-notification
    const trend = mockTrend(n);

    return (
      <IndexTable.Row
        id={String(n.id)}
        key={n.id}
        position={index}
        selected={selectedResources.includes(String(n.id))}
      >
        <IndexTable.Cell>
          <Text as="span" variant="bodySm" tone="subdued">
            {formatTime(n.created_at)}
          </Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <BlockStack gap="050">
            <Text as="span" fontWeight="medium" truncate>
              {n.title}
            </Text>
            <Box maxWidth="280px">
              <Text as="span" variant="bodySm" tone="subdued" truncate>
                {n.body}
              </Text>
            </Box>
          </BlockStack>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Badge tone={tone}>{n.status}</Badge>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text as="span" alignment="end" numeric>
            {n.sent_count}
          </Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <InlineStack gap="100" blockAlign="center" align="end">
            <Text as="span" numeric>
              {n.opened_count}
            </Text>
            {n.sent_count ? (
              <Text as="span" variant="bodySm" tone="subdued">
                ({openRate}%)
              </Text>
            ) : null}
          </InlineStack>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <InlineStack gap="100" blockAlign="center" align="end">
            <Text as="span" numeric>
              {n.clicked_count}
            </Text>
            {n.sent_count ? (
              <Text as="span" variant="bodySm" tone="subdued">
                ({clickRate}%)
              </Text>
            ) : null}
          </InlineStack>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text as="span" alignment="end" numeric tone={n.converted_count > 0 ? 'success' : undefined}>
            {n.converted_count}
          </Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <SparklineCell values={trend} color={n.converted_count > 0 ? '#008060' : '#5C6AC4'} />
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  return (
    <IndexTable
      resourceName={resourceName}
      itemCount={notifications.length}
      selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
      onSelectionChange={handleSelectionChange}
      headings={[
        { title: 'الإرسال' },
        { title: 'الإشعار' },
        { title: 'الحالة' },
        { title: 'مبعوت', alignment: 'end' },
        { title: 'فتح', alignment: 'end' },
        { title: 'ضغط', alignment: 'end' },
        { title: 'Conv.', alignment: 'end' },
        { title: 'الاتجاه' },
      ]}
      selectable={false}
    >
      {rows}
    </IndexTable>
  );
}

function mockTrend(n: NotificationItem): number[] {
  // Generate a deterministic 7-point sparkline using the notification's
  // sent/opened/clicked totals as seed. Looks alive without a real
  // time-series.
  if (!n.sent_count) return [0, 0, 0, 0, 0, 0, 0];
  const seed = (n.opened_count * 7 + n.clicked_count * 13 + n.id) % 100;
  const base = n.sent_count / 7;
  return Array.from({ length: 7 }, (_, i) => {
    const wave = Math.sin(((i + seed) / 7) * Math.PI * 2) * 0.4 + 0.7;
    return Math.max(0, Math.round(base * wave));
  });
}
